<template>
  <!-- `data-nav-header` 는 renewal 의 nav-menu.js 가 "패널이 열렸을 때 배경을 맞출 헤더"를
       찾는 표식이다. renewal 단독 사이트에서는 `.header` 를 찾지만 여기선 우리 헤더다. -->
  <header class="headerMain" :class="{ 'locale-pending': !localeReady }" data-nav-header>
    <div class="container flex" @click.stop="isShowMobileMenu = false">
      <div class="leftMenu flex">
        <NuxtLink to="/" class="headerLogo">
          <img src="~/assets/images/logo_blue.svg" alt="codepresso's logo image">
        </NuxtLink>
        <!-- 상단 메뉴와 드롭다운은 renewal 것을 그대로 쓴다(마크업·클래스 무수정).
             `.renewal-root` 안에서만 renewal CSS 가 살아나므로 헤더 나머지에는 영향이 없다. -->
        <!-- 900px 이하에서는 이 자리에 두지 않는다 — 같은 SiteNav 를 모바일 드로어가
             렌더하므로, 둘 다 마운트하면 nav-panel id 가 중복된다(aria-controls 가 깨진다). -->
        <div v-if="!isMobileNav" class="headerRenewalNav renewal-root" @click="onClickRenewalLink">
          <SiteNav />
        </div>
      </div>
      <div class="rightMenu flex">
        <ul class="btnArea flex">
          <LazyUserCommonCreditInfo v-if="isAdmin" :is-main="true" />
          <!-- Auth-state CTAs render only once `getUserData()` has resolved,
               to avoid the OAuth-callback flash where the header briefly shows
               "Sign in" before flipping to "Dashboard" once userStore is
               populated. Header may render before auth resolves on public
               marketing routes (Home / Policy) per app.vue's isReady gate. -->
          <template v-if="userStore.isAuthResolved">
            <li>
              <NuxtLink v-if="!isLoginUser" to="/sign" class="btn whiteLineBtn">
                {{ t("button.sign") }}
              </NuxtLink>
              <NuxtLink v-if="isLoginUser" to="/dashboard" class="btn indigoBtn">
                {{ t("common-dashboard") }}
              </NuxtLink>
            </li>
            <li v-if="!isLoginUser">
              <NuxtLink to="/home/request/demo" class="btn blueBtn">
                {{ t("button.demo") }}
              </NuxtLink>
            </li>
            <li v-else ref="profileArea" class="profileBtn" @click.stop="onToggleProfile">
              <LazyUserCommonProfileButton :is-profile-open-flag="isProfileOpenFlag" />
            </li>
          </template>
        </ul>
        <button
          type="button"
          class="ham"
          :class="{on: isShowMobileMenu}"
          @click.stop="methods.onClickMobileMenuButton">
          <span></span>
        </button>
      </div>
      <LazyHomeHeaderMobile
        :class="{ on: isShowMobileMenu}"
        :use-renewal-nav="isMobileNav"
        :menu-list="menuList"
        :is-login-user="isLoginUser"
        @on-click-menu="methods.onClickMenu"
        @on-click-sub-menu="methods.onClickSubMenu"
        @reset-menu-list="methods.resetMenuList"
      />
      <div
        class="navBg"
        :class="{ on: isShowMobileMenu }"
        @click.stop="isShowMobileMenu = false" />
    </div>
  </header>
</template>

<script setup lang="ts">
import {nextTick, onMounted, ref, useRoute, watch} from '#imports';
import {useI18n} from "vue-i18n";
import type {PropType} from "vue";
import {computed} from 'vue';
import type {HomeMenuType} from "~~/package/config/HomeSubMenuConfig";
import {onClickOutside, useMediaQuery} from "@vueuse/core";
import {useUserStore} from "~~/application/store/user/userStore";
import SiteNav from "~~/codepresso-renewal/components/SiteNav.vue";
import {useRenewalLinkInterception} from "~/composables/useRenewalLinkInterception";
// GNB 전용 스코프 CSS. 모든 셀렉터가 `.renewal-root` 를 요구하므로 헤더 나머지에는 닿지 않는다.
import "~~/codepresso-renewal/embed/css/nav.css";

const {t} = useI18n({useScope: 'local'})
// renewal GNB 의 링크는 평범한 <a href> 다. DOM 을 고치지 않고 클릭만 가로채
// SPA 내비게이션으로 바꾼다.
const onClickRenewalLink = useRenewalLinkInterception();

// GNB 를 세로 드로어로 접는 기준. renewal 의 `css/mobile.css` 가 같은 값(900px)에서
// 메뉴를 세로로 바꾸므로 여기서도 그 값을 쓴다 — 어긋나면 그 구간에서 가로 메뉴가
// 세로 드로어 안에 들어가 깨진다. 서버에서는 false 라 프리렌더에 데스크톱 GNB 가 실린다.
const isMobileNav = useMediaQuery('(max-width: 900px)');
const props = defineProps({
  menuList: { type: Array as PropType<HomeMenuType[]>, default: () => [] },
  isLoginUser: { type: Boolean, default: false }
});
const emit = defineEmits(["onClickMenu", "onClickSubMenu", "resetMenuList"]);
const route = useRoute();
const userStore = useUserStore();

// Hide the header until after Vue mounts on the client and nuxt-i18n has
// applied the cookie-detected locale. Otherwise the static prerender (default
// locale = 'en') flashes English menu items / button labels before they swap
// to the user's actual locale (ko/ja/...). Pure visibility — layout space
// is reserved (no CLS).
const localeReady = ref(false);
onMounted(() => { nextTick(() => { localeReady.value = true; }); });

const isShowMobileMenu = ref(false);
const isProfileOpenFlag = ref(false);
const profileArea = ref(null);

const isAdmin = computed(() =>
  userStore.isAdminType || userStore.isSuperAdminType
)

const onToggleProfile = () => {
  isShowMobileMenu.value = false;
  isProfileOpenFlag.value = !isProfileOpenFlag.value;
}

const methods = {
  onClickMenu(menuTitle: string) {
    emit("onClickMenu", menuTitle, "header");
  },
  onClickSubMenu(subMenuTitle: string) {
    emit("onClickSubMenu", subMenuTitle);
  },
  resetMenuList() {
    emit("resetMenuList");
  },
  onClickMobileMenuButton() {
    isShowMobileMenu.value = !isShowMobileMenu.value;
  }
};


watch(route, () => {
  // 이동하면 모바일 드로어를 닫는다. SPA 이동은 DOM 을 다시 만들지 않아 열린 채로 남는다.
  isShowMobileMenu.value = false;

  if (route.path === "/") {
    methods.resetMenuList();
  } else {
    const strSplit = route.path.split("/");
    methods.onClickSubMenu(strSplit[2] === undefined ? "undefined" : strSplit[2]);
  }
}, { immediate: true });

onClickOutside(profileArea, () => {
  isProfileOpenFlag.value = false;
});
</script>

<!-- 상단 메뉴 문구는 여기 없다 — 데스크톱 GNB 는 renewal 의 SiteNav 가 자기 사전으로
     렌더하고, 모바일 메뉴는 HomeHeaderMobile 이 자기 <i18n> 을 쓴다. -->
<i18n>
{
  "en": {
    "button": {
      "sign": "Log In",
      "demo": "Request a Demo"
    }
  },
  "ko": {
    "button": {
      "sign": "로그인",
      "dashboard": "대시보드",
      "demo": "무료 체험 신청"
    }
  },
  "ja": {
    "button": {
      "sign": "ログイン",
      "dashboard": "ダッシュボード",
      "demo": "デモをリクエスト"
    }
  }
}
</i18n>
<style lang="scss">
@import "~/assets/scss/abstracts/variables.scss";
@import "~/assets/scss/abstracts/mixin.scss";
@import "~/assets/scss/header.scss";

// Suppress the static-prerender locale flash. Set in script setup via `nextTick(() => localeReady.value = true)`.
// Uses `visibility` so layout space is preserved (no CLS), only text content is hidden.
//
// 가리는 것은 **언어가 바뀔 방문자에게만** 필요하다. 프리렌더는 기본 언어(en)로 구워지고,
// 헤더는 Lazy 라 문서가 공개된 뒤에야 마운트되므로 그 사이 영어 메뉴가 스쳐 보인다.
// 언어가 프리렌더와 같으면 바뀔 글자가 없어 가릴 이유도 없다. 예전엔 조건 없이 가려서
// 헤더가 hydration 완료까지 숨겨졌고, renewal 콘텐츠가 커진 뒤로는 그게 약 1.7초였다.
// `data-locale-swap` 은 nuxt.config.ts 의 인라인 anti-FOUC 스크립트가 첫 페인트 전에
// 달아 두고 지우지 않는다.
html[data-locale-swap] .headerMain.locale-pending { visibility: hidden; }

// ─── renewal GNB ────────────────────────────────────────────────────────────
// 상단 메뉴와 메가패널은 renewal 것을 그대로 쓴다. renewal 의 CSS·DOM 은 고치지 않고,
// 여기서는 우리 헤더 쪽 사정(자리·모바일 분기·열림 상태)만 맞춘다.
// 메뉴 자체의 CSS(embed/css/nav.css)는 script setup 에서 import 한다 — Sass 를 거치면
// `.css` 확장자가 plain @import 로 남아 경로 해석이 어긋난다.

.headerMain {
  // 상단 메뉴 줄의 생김새는 우리 헤더 것이다(요구 1·3). renewal 에서 가져오는 것은
  // 메뉴 항목과 hover 시 열리는 메가패널이고, 글자·간격·색·활성 표시는 기존 `.navMain`
  // 값을 그대로 쓴다. 아래 값은 교체 전 화면에서 실측한 것이다:
  //   ul gap 48px (≤1240px 24px) / 16px·500·#000 / li padding 5px 0 / 활성 rgb(26,97,234)
  .headerRenewalNav {
    flex: 1;

    // renewal 의 메가패널은 position: fixed 로 뷰포트 기준이라 여기에 두어도 정상 배치된다.
    // 다만 시작 위치는 --header-height 가 정하므로 우리 헤더 높이(.container 80px)와 맞춘다.
    --header-height: 80px;

    .nav-list {
      gap: 48px;
      @include mq(screen-1240) {
        gap: 24px;
      }
    }

    .nav-item {
      @include paddingBox(5px 0);
    }

    .nav-trigger,
    .nav-link {
      gap: 6px; // 기존 `.menuTit .arrow { margin-left: 6px }`
      font-size: 16px;
      font-weight: 500;
      line-height: 1.12;
      letter-spacing: normal;
      color: #000;
      transition: all 0.1s ease-in-out;
    }

    // 셰브론은 글자와 같은 색으로 따라간다(기존 arrow 의 path fill 이 글자색과 같았다).
    .nav-chevron {
      min-width: 16px;
      width: 16px;
      height: 16px;
      color: inherit;
    }

    // hover 와 "지금 보고 있는 메뉴" 를 같은 파란색으로. 활성 표시는 renewal SiteNav 가
    // 현재 경로를 보고 `is-active` 를 붙여 주고, 색은 여기서 정한다.
    .nav-item:hover > .nav-trigger,
    .nav-item.is-active > .nav-trigger,
    .nav-link:hover,
    .nav-link.is-active {
      color: $color-main;
    }
  }

  // 메가패널이 열려 있는 동안 헤더 배경을 패널과 같은 흰 서피스로 맞춘다.
  // (renewal 의 nav-menu.js 가 [data-nav-header] 에 붙이는 클래스)
  &.is-nav-open {
    background: #fff;
    border-bottom-color: transparent;
  }
}

// ─── 900~1080px: 데스크톱 표현을 유지한다 ──────────────────────────────────
// 우리 헤더는 원래 1080px 에서 햄버거로 넘어가지만, GNB 는 renewal 것을 쓰고 renewal 의
// 세로 드로어 CSS 는 900px 부터다. 1080 에서 접으면 900~1080 구간에서 가로 메뉴가 세로
// 드로어 안에 들어가 깨진다. 그래서 이 구간만 데스크톱 쪽으로 되돌린다 —
// 전 페이지 공용인 `_header.scss` 는 건드리지 않고 여기서 덮는다.
@media screen and (min-width: 901px) and (max-width: 1080px) {
  .headerMain {
    // 햄버거 대신 가로 GNB 를 그대로 둔다.
    .ham { display: none; }
    .navMain.mobile { display: none; }

    // `_header.scss` 의 `.leftMenu nav { display: none }` 은 옛 `<nav class="navMain">`
    // 을 겨냥한 것인데, renewal 이 렌더하는 `<nav class="nav">` 까지 같이 잡는다.
    .headerRenewalNav .nav { display: block; }

    // `_header.scss` 가 1080 이하에서 감추는 로그인·데모 버튼을 되살린다.
    // 이 구간에는 햄버거가 없어서, 감춘 채 두면 로그인 진입점이 사라진다.
    .rightMenu > .btnArea > li { display: flex; }
  }
}

// ─── 다크 페이지에서의 헤더 ──────────────────────────────────────────────────
// AXpresso 계열 페이지(/axpresso, /ax-build, /ax-grow)는 renewal 이 <main> 에
// `main-dark` 를 붙여 짙은 남색 배경으로 그린다. 그 위에 밝은 헤더가 얹히면 헤더만
// 동떨어져 보이므로, 페이지 맨 윗부분과 같은 색으로 맞추고 글자를 흰색으로 뒤집는다.
//
// 어떤 페이지가 다크인지는 renewal 이 정한다 — 목록을 여기 복사해 두면 renewal 이
// 페이지를 추가·변경할 때 어긋난다. `:has()` 로 "지금 콘텐츠가 다크인가"를 직접 본다.
// 값은 renewal 토큰과 같다: 배경 --color-axpresso-bg-1(#050C21), 글자 --color-surface.
#wrapper:has(.renewal-page .main-dark) {
  .headerMain {
    // 맨 위에서는 배경을 깔지 않는다. 기존 헤더가 그랬고(투명 → 스크롤하면 `.on` 이
    // 흰 면을 깐다), 페이지 상단의 그라데이션이 헤더 뒤로 그대로 비쳐야 한다.
    // 글자만 바로 흰색으로 뒤집는다 — 뒤에 깔린 페이지가 이미 짙기 때문이다.
    border-bottom-color: transparent;

    .headerRenewalNav {
      .nav-trigger,
      .nav-link {
        color: #fff;
      }

      // 다크 위에서는 기존 포인트 블루가 묻힌다. 밝은 쪽으로 올려 대비를 맞춘다.
      .nav-item:hover > .nav-trigger,
      .nav-item.is-active > .nav-trigger,
      .nav-link:hover,
      .nav-link.is-active {
        color: color-mix(in srgb, $color-main 45%, white);
      }
    }

    // 관리자에게만 보이는 "평가 초대 크레딧". `_header.scss` 가 `color: $color-black` 로
    // 두는데 다크 배경에서는 글자가 묻혀 안 보인다. 헤더의 다른 글자와 같은 흰색으로.
    // 안쪽 숫자·버튼은 포인트 블루($color-main)라 다크에서도 읽힌다 — 그대로 둔다.
    .infoBtn {
      color: #fff;

      // 오른쪽 구분선도 같이 올린다. 밝은 면 기준 색이라 다크에서는 보이지 않는다.
      &::before {
        background: rgba(255, 255, 255, 0.24);
      }
    }

    // 메가패널은 흰 면이므로, 열리는 동안에는 헤더도 흰 면으로 돌아가 한 덩어리가 된다
    // (renewal 이 `.header.is-nav-open` 에서 하는 것과 같은 의도).
    &.is-nav-open {
      background: #fff;

      // 패널이 열리면 헤더가 흰 면이 되므로 크레딧 글자도 원래 색으로 돌린다.
      .infoBtn {
        color: $color-black;

        &::before { background: $color-main-20; }
      }

      .headerRenewalNav {
        .nav-trigger,
        .nav-link {
          color: #000;
        }

        .nav-item:hover > .nav-trigger,
        .nav-item.is-active > .nav-trigger,
        .nav-link:hover,
        .nav-link.is-active {
          color: $color-main;
        }
      }
    }
  }

  // 스크롤해서 헤더가 콘텐츠 위에 떠 있을 때만 면을 깐다. 기본값은 흰 면인데
  // (_header.scss) 다크 페이지에서는 그 흰 면만 동떨어져 보이므로 페이지 맨 윗부분과
  // 같은 색으로 바꾼다.
  //
  // 스크롤 표시 `.on` 은 헤더가 아니라 래퍼(HomeCommonLayout 의 `.headerWrap`)에 붙는다.
  // 래퍼 없이 헤더만 쓰는 화면을 위해 `_header.scss` 는 `.headerMain.on` 도 함께 두고
  // 있으므로 여기서도 둘 다 받는다.
  //
  // 패널이 열려 있을 때는 빼 둔다 — 그때는 위의 `.is-nav-open` 규칙대로 패널과 같은
  // 흰 면이어야 한다.
  .headerWrap.on .headerMain:not(.is-nav-open),
  .headerMain.on:not(.is-nav-open) {
    background: #050C21;
    border-bottom-color: transparent;
  }
}
</style>